/**
 * Settings → Filters (web.md §4, fix 4): filters in evaluation order (then archived ones, restorable),
 * each edited in the shared `RuleEditor` — name, icon, trigger folder, groups (ALL/ANY inside, AND/OR
 * across), actions, Test rule, Run now — plus its position with Move up / Move down. A filter cannot use
 * folder, topic or score conditions (the field list comes from core's ALLOWED_FIELDS).
 */

import type { Rule, RuleId } from '@digester/core';
import { reportError } from '../../app/errors.js';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { useFilterRules, useTags } from '../rules/data.js';
import { newRuleDraft, ruleSummary, toDraft, type RuleDraft } from '../rules/model.js';
import { ArchivedNote, OrderSection } from '../rules/OrderSection.js';
import { RuleEditor } from '../rules/RuleEditor.js';
import { saveRuleDraft } from '../rules/save.js';
import '../rules/rules.css';
import { EditorEmpty, EditorFrame, useDraft, type ItemsResult, type SettingsCategory, type SettingsEditorProps } from '../shell/index.js';

function useItems(): ItemsResult {
  const { rules, isLoading } = useFilterRules();
  return {
    items: rules.map((r) => ({ id: r.id, name: r.name, icon: r.icon, subtitle: ruleSummary(r), enabled: r.enabled, ...(r.archivedAt !== null ? { status: 'Archived' } : {}) })),
    isLoading,
    error: null,
  };
}

function FilterEditor({ recordId, duplicateOf }: SettingsEditorProps) {
  const { rules, isLoading } = useFilterRules();
  const tags = useTags();
  if (!recordId) return <EditorEmpty text="No filters yet" />;
  if (isLoading) return <EditorEmpty text="Loading…" />;
  if (recordId === 'new') {
    const from = duplicateOf ? rules.find((r) => r.id === duplicateOf) : undefined;
    const initial: RuleDraft = from ? { ...toDraft(from, tags), name: `${from.name} copy`.slice(0, 80) } : newRuleDraft('filter', 'inbox');
    return <FilterForm rule={null} initial={initial} rules={rules} />;
  }
  const rule = rules.find((r) => r.id === recordId);
  if (!rule) return <EditorEmpty />;
  return <FilterForm rule={rule} initial={toDraft(rule, tags)} rules={rules} />;
}

function FilterForm({ rule, initial, rules }: { rule: Rule | null; initial: RuleDraft; rules: readonly Rule[] }) {
  const engine = useEngine();
  const tags = useTags();
  const draft = useDraft<RuleDraft>({ initial, isNew: rule === null, onSave: (v) => saveRuleDraft(engine, rule, v, tags) });
  const v = draft.value;
  const archived = rule !== null && rule.archivedAt !== null;
  const restore = async (): Promise<void> => {
    if (!rule) return;
    try {
      await engine.rules.restore(rule.id);
    } catch (error) {
      reportError(error);
    }
  };
  return (
    <EditorFrame
      title={v.name.trim() || rule?.name || 'New rule'}
      icon={v.icon}
      {...(archived ? { status: { label: 'Archived', on: false } } : { enabled: { checked: v.enabled, onChange: (enabled: boolean) => draft.set((d) => ({ ...d, enabled })) } })}
    >
      {archived ? <ArchivedNote onRestore={() => void restore()} /> : null}
      <RuleEditor value={v} set={draft.set} ruleId={archived ? null : (rule?.id ?? null)} dirty={draft.dirty}>
        {rule && !archived ? <OrderSection kind="filter" rule={rule} rules={rules} help="Filters run top to bottom; the first one that moves or deletes a message decides where it goes." /> : null}
      </RuleEditor>
    </EditorFrame>
  );
}

export const category: SettingsCategory = {
  id: 'filters',
  name: 'Filters',
  icon: 'list-filter',
  useItems,
  Editor: FilterEditor,
  newLabel: 'New filter',
  duplicate: true,
  archive: (engine, item) => engine.rules.archive(item.id as RuleId),
  archiveNote: 'Messages it already moved stay where they are, with their history.',
};
